import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import CheckRounded from '@mui/icons-material/CheckRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import { DataTable, DownloadButton, InfoHint, Panel, ValueWithDelta, type DataTableColumn } from '../../../../ui-kit';
import type { PlannerDay } from '../../../../types/plannerDashboard';
import { money, twoDecimal, twoDecimalPercent, whole } from '../plannerFormat';

/** A column heading with its explanation behind an info icon. */
function Heading({ label, info }: { label: string; info: React.ReactNode }) {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {label}
      <InfoHint title={info} label={`About ${label.toLowerCase()}`} />
    </Box>
  );
}

interface DaysTableProps {
  days: PlannerDay[];
  target: number;
  leadsPerAgent: number;
  loading?: boolean;
  onDownload: () => void;
  downloading?: boolean;
  problem?: string | null;
}

/** The plan checked for each day of the week. */
export default function DaysTable({ days, target, leadsPerAgent, loading, onDownload, downloading, problem }: DaysTableProps) {
  const columns: DataTableColumn<PlannerDay>[] = [
    // The day name stays put while the rest scrolls sideways: without it a
    // figure three groups along belongs to no day the reader can see.
    {
      key: 'day',
      header: 'Day',
      width: 130,
      sticky: true,
      render: (d) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{d.name}</Typography>,
    },
    {
      key: 'leads_in',
      group: 'Actual',
      header: <Heading label="Leads in" info="How many new leads come in on this day of the week, on average, from the channels and dates you picked: every lead, MQL or not. For Monday: all Monday leads ÷ the number of Mondays." />,
      numeric: true,
      render: (d) => twoDecimal(d.leads_in),
    },
    {
      key: 'mqls_in',
      group: 'Actual',
      header: <Heading label="MQLs in" info="How many of those leads are marked MQL, on average, We're Sorry and Alt Mon included." />,
      numeric: true,
      render: (d) => twoDecimal(d.mqls_in),
    },
    {
      key: 'available',
      group: 'Actual',
      header: <Heading label="Sales Leads in" info="How many of those leads are Sales Leads, on average. A Sales Lead is marked MQL and is not We're Sorry or Alt Mon. Sales Leads are what agents are given, so contacted, submits and conversion count only these." />,
      numeric: true,
      render: (d) => twoDecimal(d.sales_leads_available),
    },
    {
      key: 'contacted_all',
      group: 'Actual',
      header: <Heading label="Contacted" info="Of every Sales Lead that came in that day, how many an agent spoke with — a call that lasted 90 seconds or more. A daily average, whoever worked them." />,
      numeric: true,
      render: (d) => twoDecimal(d.contacted_available),
    },
    {
      key: 'submits_all',
      group: 'Actual',
      header: <Heading label="Submits" info="Of the Sales Leads an agent spoke with that day, how many sent an application to the carrier (CAS). A daily average, whenever the application landed." />,
      numeric: true,
      render: (d) => twoDecimal(d.submits_available),
    },
    {
      key: 'conversion_all',
      group: 'Actual',
      header: <Heading label="Conversion" info="Submits ÷ contacted, for every Sales Lead that came in that day: of the Sales Leads an agent spoke with, the share that sent an application. Whoever worked them." />,
      numeric: true,
      render: (d) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{twoDecimalPercent(d.conversion_available)}</Typography>,
    },
    {
      key: 'mql_to_cas',
      group: 'Actual',
      header: <Heading label="MQL to CAS" info="Of every MQL that came in that day, We're Sorry and Alt Mon included, the share that sent an application (CAS), whether or not an agent reached them." />,
      numeric: true,
      render: (d) => twoDecimalPercent(d.mql_to_cas),
    },
    {
      key: 'sales_lead_to_cas',
      group: 'Actual',
      header: <Heading label="Sales Lead to CAS" info="Of every Sales Lead that came in that day, the share that sent an application (CAS), whether or not an agent reached them." />,
      numeric: true,
      render: (d) => twoDecimalPercent(d.sales_lead_to_cas),
    },
    {
      key: 'spend',
      group: 'Actual',
      header: <Heading label="Spend" info="What the day's leads cost to buy, on average: the lead cost recorded against every lead that came in that day of the week, Sales Lead or not. Organic and referral leads cost nothing, so they count as $0." />,
      numeric: true,
      render: (d) => money(d.spend),
    },
    {
      key: 'cost_per_lead',
      group: 'Actual',
      header: <Heading label="Cost / lead" info="Spend ÷ leads coming in: what one new lead costs on this day of the week, on average, counting every lead." />,
      numeric: true,
      render: (d) => money(d.cost_per_lead),
    },
    {
      key: 'cost_per_submit',
      group: 'Actual',
      header: <Heading label="Cost / submit" info="What every lead that came in that day cost, divided by the applications sent — whoever worked them, whenever it landed. What your plan's own Sales Leads cost per application is in the group on the right." />,
      numeric: true,
      render: (d) => money(d.cost_per_submit),
    },
    {
      key: 'short',
      group: 'Projected with your plan',
      header: <Heading label="Sales Leads short" info="How many Sales Leads are missing when fewer come in than your plan needs. Some agents would not get a full load that day. A dash means there are enough." />,
      numeric: true,
      render: (d) =>
        d.short_by >= 0.5 ? (
          <Box component="span" sx={(t) => ({ color: t.tokens.semantic.warning, fontWeight: 600 })}>{twoDecimal(d.short_by)}</Box>
        ) : (
          <Box component="span" sx={(t) => ({ color: t.tokens.text.tertiary })}>—</Box>
        ),
    },
    {
      key: 'campaigns',
      group: 'Projected with your plan',
      header: <Heading label="Campaigns needed" info="How many campaigns it takes to fill the day. Agents get Sales Leads from the best campaign first, then the next best, until the Sales Leads needed are covered. More campaigns usually means weaker ones are included." />,
      numeric: true,
      render: (d) => whole(d.campaigns_used),
    },
    {
      key: 'reached',
      group: 'Projected with your plan',
      header: <Heading label="Contacted" info="Of the Sales Leads your plan hands out, how many an agent actually spoke with — a call that lasted 90 seconds or more. A daily average." />,
      numeric: true,
      render: (d) => twoDecimal(d.reached),
    },
    {
      key: 'applied',
      group: 'Projected with your plan',
      header: <Heading label="Submits" info="Of the Sales Leads your plan hands out that an agent spoke with, how many sent an application to the carrier (CAS). A daily average, counted whenever the application landed." />,
      numeric: true,
      render: (d) => twoDecimal(d.applied),
    },
    {
      key: 'plan_cost_per_submit',
      group: 'Projected with your plan',
      header: <Heading label="Cost / submit" info="What the campaigns your plan uses cost, divided by the applications their Sales Leads send. A campaign's spend pays for all its leads, Sales Lead or not, so the share of its Sales Leads the plan takes carries the same share of its spend. Add agents and the plan reaches weaker campaigns, so this usually rises. In brackets: how far that sits from the cost across every lead that came in." />,
      numeric: true,
      render: (d) => (
        <ValueWithDelta
          value={d.plan_cost_per_submit}
          against={d.cost_per_submit}
          lowerIsBetter
          label="Cost per submit against all leads"
        >
          {money(d.plan_cost_per_submit)}
        </ValueWithDelta>
      ),
    },
    {
      key: 'conversion',
      group: 'Projected with your plan',
      header: <Heading label="Conversion" info="Submits ÷ contacted: of the Sales Leads your plan hands out, the share an agent reached that sent an application. Because the best campaigns are used first, this is the best this day can do with your plan. In brackets: how far that sits from every Sales Lead that came in that day." />,
      numeric: true,
      render: (d) => (
        <ValueWithDelta value={d.conversion} against={d.conversion_available} label="Conversion against all Sales Leads">
          <Box component="span" sx={{ fontWeight: 600 }}>{twoDecimalPercent(d.conversion)}</Box>
        </ValueWithDelta>
      ),
    },
    {
      key: 'target',
      group: 'Projected with your plan',
      header: (
        <Heading
          label={`vs ${whole(target)}% target`}
          info={`Whether the day's contact to submit reaches your ${whole(target)}% target. If not, how many percentage points it falls short — "2.40 under" means ${twoDecimal(Math.max(0, target - 2.4))}%.`}
        />
      ),
      render: (d) => {
        const gap = d.conversion === null ? null : target - d.conversion;
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={(t) => ({
              color: d.hits_target ? t.tokens.semantic.success : t.tokens.semantic.danger,
              whiteSpace: 'nowrap',
              '& svg': { fontSize: t.typography.body2.fontSize },
            })}
          >
            {d.hits_target ? <CheckRounded /> : <CloseRounded />}
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
              {d.hits_target ? 'Hits' : gap === null ? 'No Sales Leads contacted' : `${twoDecimal(Math.max(0.01, gap))} under`}
            </Typography>
          </Stack>
        );
      },
    },
    {
      key: 'most',
      group: 'Projected with your plan',
      header: (
        <Heading
          label="Max agents"
          info={`The largest team this day can support: each agent still gets ${whole(leadsPerAgent)} Sales Leads and the day still hits ${whole(target)}% contact to submit. One more agent would mean too few Sales Leads or contact to submit under the target.`}
        />
      ),
      numeric: true,
      render: (d) => whole(d.most_agents),
    },
  ];

  const table = (fill: boolean) => (
    <DataTable
      ariaLabel="The plan by day of the week"
      columns={columns}
      groups={[{ key: 'days', title: '', rows: days }]}
      getRowKey={(d) => String(d.dow)}
      collapsibleGroups={false}
      fill={fill}
      rowTone={(d) => (d.hits_target ? null : 'danger')}
      emptyMessage="No leads in this date range."
    />
  );

  return (
    <Panel
      title="By day of the week"
      subtitle="Your plan checked for each day. Agents get Sales Leads from that day's best campaigns first, until everyone has their share."
      actions={
        <DownloadButton
          formats={[{ key: 'xlsx', label: 'Excel (.xlsx)', onDownload }]}
          tooltip="Download this table as Excel"
          loading={downloading}
          disabled={loading}
        />
      }
      loading={loading}
      loadingHeight={320}
      flush
      footer={
        problem ?? (
          <>
            Every figure is a daily average for that day of the week across the date range.
            {' '}Leads are every lead that came in, MQLs the ones marked MQL, and Sales Leads the MQLs that are not We&apos;re Sorry or Alt Mon.
            {' '}<strong>Projected with your plan</strong> is your plan applied to past Sales Leads: what the Sales Leads
            it hands out actually did, if agents had taken the best campaigns first.
          </>
        )
      }
      expanded={{ content: table(true) }}
    >
      {table(false)}
    </Panel>
  );
}
